import { FirebaseError } from '@firebase/util';
import { kebabCase } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../data/context/app-context';
import { FormMessage, FormMessageType } from '../../../data/utils/form.utils';
import { useQueryParams } from '../../../data/utils/use-query-params';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../elements/button/button';
import { InputFieldController } from '../../../elements/form/controllers/input-field';
import { FormRow } from '../../../elements/form/form';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import { Spinner } from '../../../elements/spinner/spinner';
import { useResetPasswordForm } from './form/use-reset-password-form';

export function ResetPassword() {
  const intl = useIntl();

  const { validating, recoverPassword, checkOOBCode } = useAppContext();

  const { mode = null, oobCode = null } = useQueryParams();

  const { form, formIsValid } = useResetPasswordForm();

  const [loading, setLoading] = useState<boolean>(false);

  const [formMessage, setFormMessage] = useState<FormMessage | null>(null);

  useEffect(() => {
    (async () => {
      if (mode === 'resetPassword' && oobCode) {
        const isOOBCodeValid = await checkOOBCode(oobCode);
        if (!isOOBCodeValid) {
          setFormMessage({
            type: FormMessageType.ERROR,
            message: intl.formatMessage({
              id: 'common.errors.auth-invalid-reset-code',
            }),
          });
        }
      }
    })();
  }, [checkOOBCode, intl, mode, oobCode]);

  const onHandleSubmit = useCallback(async () => {
    form.handleSubmit(async () => {
      if (oobCode && mode === 'resetPassword') {
        const values = form.getValues();

        setLoading(true);

        setFormMessage(null);

        try {
          const isOOBCodeValid = await checkOOBCode(oobCode);
          if (isOOBCodeValid) {
            const result = await recoverPassword(
              oobCode,
              values.password,
              values.passwordConfirm
            );

            if (!result.success) {
              setFormMessage({
                type: FormMessageType.ERROR,
                message: intl.formatMessage({
                  id: `common.errors.${kebabCase(
                    result.message ?? 'unknown-error'
                  )}`,
                  defaultMessage: result.message ?? 'unknown-error',
                }),
              });
            } else {
              setFormMessage({
                type: FormMessageType.SUCCESS,
                message: intl.formatMessage({
                  id: 'auth.reset-password.success',
                }),
              });
            }
          } else {
            setFormMessage({
              type: FormMessageType.ERROR,
              message: intl.formatMessage({
                id: 'common.errors.auth-invalid-reset-code',
              }),
            });
          }
        } catch (error) {
          if (error instanceof FirebaseError) {
            setFormMessage({
              type: FormMessageType.ERROR,
              message: intl.formatMessage({
                id: `common.errors.${kebabCase(error.code ?? 'unknown-error')}`,
                defaultMessage: error.code ?? 'unknown-error',
              }),
            });
          } else {
            setFormMessage({
              type: FormMessageType.ERROR,
              message: (error as Error).message,
            });
          }
        }
      }

      setLoading(false);
    })();
  }, [form, oobCode, mode, checkOOBCode, recoverPassword, intl]);

  return validating ? (
    <Spinner />
  ) : (
    <>
      <h2>
        <CustomFormattedMessage id="auth.reset-password.title" />
      </h2>

      {formMessage?.type === FormMessageType.SUCCESS ? (
        <p className="form-success">{formMessage.message}</p>
      ) : (
        <>
          {formMessage?.type === FormMessageType.ERROR && (
            <p className="form-error">{formMessage.message}</p>
          )}

          <FormProvider {...form}>
            <form onSubmit={onHandleSubmit}>
              <FormRow>
                <InputFieldController
                  type="password"
                  name="password"
                  label={<CustomFormattedMessage id="common.new-password" />}
                  placeholder={intl.formatMessage({
                    id: 'common.new-password',
                  })}
                />
              </FormRow>

              <FormRow>
                <InputFieldController
                  type="password"
                  name="passwordConfirm"
                  label={
                    <CustomFormattedMessage id="common.confirm-new-password" />
                  }
                  placeholder={intl.formatMessage({
                    id: 'common.confirm-new-password',
                  })}
                />
              </FormRow>

              <FormRow>
                <p>
                  <Button
                    useAsSubmit
                    type={ButtonType.SECONDARY}
                    icon={loading ? ButtonIcon.LOADING : ButtonIcon.NONE}
                    disabled={!formIsValid || loading || (!oobCode && !mode)}
                    onClick={onHandleSubmit}
                  >
                    <CustomFormattedMessage id="auth.reset-password.button" />
                  </Button>
                </p>
              </FormRow>
            </form>
          </FormProvider>
        </>
      )}
    </>
  );
}
